import { gql } from "@apollo/client";

export const KPI_FIELDS_FRAGMENT = gql`
  fragment KpiFields on ClientKpi {
    id
    data_columns
    kpi_name
    equation
    friendly_name
    is_active
    is_default_kpi
    kpi_type
    northbeam_client
  }
`;

export const DATA_COLUMN_FIELDS_FRAGMENT = gql`
  fragment DataColumnFields on DataColumn {
    id
    is_ready_for_use_in_model
    column_name
    is_present
    is_from_mta
    is_from_mmm_custom_data
    is_spend
    is_target
    is_calendar_of_events
    mta_breakdowns {
      key
      value
    }
    last_synchronized
    friendly_name
    column_type
    target_typical_value
    target_price_value_per_unit_target
  }
`;

export const HISTORICAL_MIXES_FOR_CYCLE_QUERY = gql`
  query FetchHistoricalMixForCycle($id: String!, $dateRange: StringDateRange) {
    me {
      mmm {
        cycle(id: $id) {
          id
          model {
            id
            is_blessed
            model_path
          }
          historicalMix(dateRange: $dateRange) {
            id
            start_date
            end_date
            daily_data {
              date
              spend_data {
                breakdowns {
                  key
                  value
                }
                spend
                data_column_name
              }
              summary_data {
                kpi_data
                spend
              }
            }
            data {
              spend_data {
                breakdowns {
                  key
                  value
                }
                spend
                data_column_name
              }
              summary_data {
                kpi_data
                spend
                start_date
                end_date
              }
            }
          }
        }
      }
    }
  }
`;

export const BUDGET_MIXES_FOR_CYCLE_QUERY = gql`
  ${KPI_FIELDS_FRAGMENT}
  query FetchBlessedBudgetMixesForCycle($id: String!, $blessed: Boolean) {
    me {
      mmm {
        cycle(id: $id) {
          id
          model {
            id
            is_blessed
            model_path
          }
          clientSettings {
            id
            createBudgetRecommendationRunResults {
              id
              client_setting_id
              status
              modified
            }
            kpi {
              ...KpiFields
            }
            kpi_v2 {
              ...KpiFields
            }
            name
            budget_low
            budget_high
            target_spend
            profit_value_per_unit_kpi
            max_cost_per_unit_kpi
            locked
            desired_kpi_value

            spend_constraints {
              id
              feature {
                id
                column_name
                is_from_mta
                mta_breakdowns {
                  key
                  value
                }
                friendly_name
              }
              feature_v2 {
                id
                column_name
                is_from_mta
                mta_breakdowns {
                  key
                  value
                }
                friendly_name
              }
              budget_low
              budget_high
            }
          }
          budgetMixes(blessed: $blessed) {
            id
            is_blessed
            is_adopted
            is_custom
            client_setting_id
            client_setting_name
            client_setting_kpi
            pipeline_run_metadata_id
            name
            create_budget_recommendations_metadata
            data {
              kpi_name
              weekly_data {
                start_date
                end_date
                spend_data {
                  breakdowns {
                    key
                    value
                  }
                  spend
                  data_column_name
                }
                summary_data {
                  kpi_data
                  spend
                }
              }
              aggregated_data {
                spend_data {
                  breakdowns {
                    key
                    value
                  }
                  spend
                  data_column_name
                }
                summary_data {
                  kpi_data
                  spend
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const MMM_ENABLED_QUERY = gql`
  query FetchMMMEnabled {
    me {
      mmm {
        enabled
      }
    }
  }
`;

export const MMM_GLOBAL_DATA_QUERY = gql`
  ${KPI_FIELDS_FRAGMENT}
  query FetchUniversalMMMData {
    me {
      mmm {
        client {
          id
          input_google_sheet_url
        }
        availableBudgetMixTypes {
          id
          display_name
        }
        alerts {
          id
          cycle
          message
          email_sent_at
          dismissed_at
          model_datacolumns_affected
        }
        cycles {
          id
          clientSettingsCount
          model {
            id
            is_blessed
            model_path
            kpis {
              ...KpiFields
            }
            create_northbeam_mmm_run_result
            run_result {
              cycle_dataset_path
            }
          }
          date_range {
            id
            start_date
            end_date
          }
          kpis {
            ...KpiFields
          }
          state
          is_current
          is_errored
          breakdowns
          is_manually_overridden
          cycleInputStage {
            id
            has_updated_spend
            has_updated_adi
            has_updated_coe
          }
          hidden
          name
          cadenceInDays
        }
        activeClientSettings {
          id
          client_setting_date_range {
            id
            start_date
            end_date
          }
          reference_start_date
          reference_end_date
          client_id
          name
          cycle_id
        }
      }
    }
  }
`;

export const MMM_CYCLE_DATA_COLUMNS_V2_QUERY = gql`
  ${DATA_COLUMN_FIELDS_FRAGMENT}
  query FetchCycleDataColumnsV2($cycleId: String) {
    me {
      mmm {
        availableDataColumns {
          legacyDataColumns {
            ...DataColumnFields
          }
          dataColumns(cycleId: $cycleId) {
            ...DataColumnFields
          }
        }
      }
    }
  }
`;

export const MMM_KPI_DATA_QUERY = gql`
  ${KPI_FIELDS_FRAGMENT}
  query FetchKPIs($id: String) {
    me {
      mmm {
        availableKpis {
          kpis(id: $id) {
            ...KpiFields
          }
          modelKpis(id: $id) {
            ...KpiFields
          }
          cycleKpis(id: $id) {
            ...KpiFields
          }
        }
      }
    }
  }
`;

export const CLIENT_SETTINGS_FOR_CYCLE_QUERY = gql`
  ${KPI_FIELDS_FRAGMENT}
  query FetchClientSettingsForCycle($id: String!) {
    me {
      mmm {
        cycle(id: $id) {
          id
          model {
            id
            is_blessed
            model_path
          }
          date_range {
            id
          }
          clientSettings {
            id
            createBudgetRecommendationRunResults {
              id
              client_setting_id
              status
              modified
            }
            client_setting_date_range {
              id
              start_date
              end_date
            }
            cycle_id
            client_id
            reference_start_date
            reference_end_date
            spend_constraints {
              id
              feature {
                id
                column_name
                is_from_mta
                mta_breakdowns {
                  key
                  value
                }
                friendly_name
              }
              feature_v2 {
                id
                column_name
                is_from_mta
                mta_breakdowns {
                  key
                  value
                }
                friendly_name
              }
              budget_low
              budget_high
            }
            # mix_validations {
            #   id
            #   feature {
            #     id
            #     column_name
            #     is_from_mta
            #     mta_breakdowns {
            #       key
            #       value
            #     }
            #     friendly_name
            #   }
            #   percentage_low
            #   percentage_high
            # }
            kpi {
              ...KpiFields
            }
            kpi_v2 {
              ...KpiFields
            }
            name
            budget_low
            budget_high
            desired_kpi_value
            target_spend
            profit_value_per_unit_kpi
            max_cost_per_unit_kpi
            locked
          }
        }
      }
    }
  }
`;
